import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { adminGetVisitorsLive, adminGetVisitorsStats } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Users, Eye, Globe, Smartphone, Monitor, Tablet, ArrowUpRight, Loader2, RotateCw,
  TrendingUp, ExternalLink, Sparkles, Clock, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Helpers ─────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtRel = (d) => {
  if (!d) return '—';
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 3) return 'à l\u2019instant';
  if (diff < 60) return `il y a ${Math.floor(diff)}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');
const flagFromCountry = (cc) => {
  if (!cc || cc.length !== 2) return '🌍';
  const codePoints = [...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};
const DEVICE_ICONS = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };

export default function AdminVisitors() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [live, setLive] = useState(null);
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [flashKey, setFlashKey] = useState(0);  // increments on every successful refresh → triggers flash animation
  const intervalRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadAll();
    // Live refresh every 10 sec (just the live count, lightweight)
    intervalRef.current = setInterval(() => loadLive(true), 10000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, authLoading]);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const [l, s] = await Promise.all([
        adminGetVisitorsLive(),
        adminGetVisitorsStats(),
      ]);
      setLive(l.data);
      setStats(s.data);
      setLastRefresh(new Date());
      setFlashKey((k) => k + 1);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setRefreshing(false);
    }
  };

  const loadLive = async (silent = false) => {
    try {
      const l = await adminGetVisitorsLive();
      setLive(l.data);
      setLastRefresh(new Date());  // always update — so the "il y a Xs" stays accurate
      setFlashKey((k) => k + 1);   // brief flash on every refresh so admin sees it's alive
    } catch { /* ignore */ }
  };

  // Force the relative timestamp ("il y a Xs") to re-compute every second
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (authLoading || !user || !isAdmin) return null;

  if (!live || !stats) {
    return (
      <div className="min-h-screen flex bg-cream">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  const s = stats.summary;
  const liveCount = live.live_count || 0;
  const sessions = live.sessions || [];
  const totalDevice = (stats.devices || []).reduce((a, b) => a + b.count, 0) || 1;
  const totalBrowser = (stats.browsers || []).reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-5 lg:p-8 overflow-x-hidden">
        <div className="max-w-[1400px] mx-auto pl-12 lg:pl-0">

          {/* Header */}
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="font-display text-3xl font-bold text-rolex-green flex items-center gap-3">
                  <Users className="w-8 h-8 text-gold-dark" />
                  Visiteurs
                </h1>
                <div
                  key={`live-pill-${flashKey}`}
                  className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 transition-all"
                  style={{ animation: 'flashOnce 1.2s ease' }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  Live · refresh auto 10s
                </div>
                <style>{`
                  @keyframes flashOnce {
                    0%   { background-color: rgb(16 185 129 / 0.4); transform: scale(1.05); }
                    50%  { background-color: rgb(16 185 129 / 0.15); transform: scale(1); }
                    100% { background-color: rgb(236 253 245); transform: scale(1); }
                  }
                `}</style>
              </div>
              <p className="text-rolex-green/65 text-sm">
                {lastRefresh ? `Dernière mise à jour : ${fmtRel(lastRefresh)}` : 'Chargement…'} · Analyse anonyme (RGPD-friendly, pas de cookie tiers)
              </p>
            </div>
            <Button variant="outline" onClick={loadAll} disabled={refreshing} className="gap-2">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Rafraîchir
            </Button>
          </div>

          {/* LIVE counter — hero card */}
          <Card className="mb-5 border-2 border-emerald-400 bg-gradient-to-br from-emerald-50 to-emerald-100/40 shadow-xl">
            <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-600" />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-emerald-900">En ligne maintenant</p>
                </div>
                <p className="font-display text-6xl lg:text-7xl font-extrabold text-emerald-700 tabular-nums leading-none">{liveCount}</p>
                <p className="text-sm text-emerald-800/85 mt-2">
                  {liveCount === 0 ? 'Aucun visiteur actif' : `visiteur${liveCount > 1 ? 's' : ''} actif${liveCount > 1 ? 's' : ''} dans les 5 dernières minutes`}
                </p>
              </div>
              {live.live_pages && live.live_pages.length > 0 && (
                <div className="max-w-md bg-white rounded-xl p-4 border border-emerald-200 min-w-[260px]">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-900 mb-2">Pages actives</p>
                  {live.live_pages.slice(0, 5).map((p, i) => (
                    <div key={i} className="flex justify-between items-center py-1 text-xs">
                      <span className="font-mono text-rolex-green/85 truncate flex-1">{p.path || '/'}</span>
                      <Badge className="bg-emerald-500 text-white border-none">{p.live}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <KpiCard label="Aujourd'hui" value={fmt(s.today?.visitors)} sub={`${fmt(s.today?.pageviews)} pages vues`} icon={<TrendingUp className="w-5 h-5 text-emerald-600" />} tone="emerald" />
            <KpiCard label="Cette semaine" value={fmt(s.week?.visitors)} sub={`${fmt(s.week?.pageviews)} pages vues`} icon={<Eye className="w-5 h-5 text-gold-dark" />} tone="gold" />
            <KpiCard label="Ce mois-ci" value={fmt(s.month?.visitors)} sub={`${fmt(s.month?.pageviews)} pages vues`} icon={<Globe className="w-5 h-5 text-blue-600" />} tone="blue" />
            <KpiCard label="Total visiteurs" value={fmt(s.all_time?.visitors)} sub="depuis le lancement" icon={<Users className="w-5 h-5" />} tone="dark" />
          </div>

          {/* 30-day sparkline */}
          <Card className="mb-6 border border-gold/20 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gold-dark" /> Visiteurs uniques — 30 derniers jours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Sparkline points={stats.sparkline || []} />
            </CardContent>
          </Card>

          {/* Live sessions list + Top pages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card className="border border-gold/20 bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-600" /> Sessions en cours ({sessions.length})
                </CardTitle>
                <p className="text-xs text-rolex-green/55">Détail des visiteurs actifs dans les 5 dernières minutes</p>
              </CardHeader>
              <CardContent className="p-0 max-h-[400px] overflow-y-auto">
                {sessions.length === 0 ? (
                  <div className="p-10 text-center text-rolex-green/55">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Aucun visiteur actif</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gold/10">
                    {sessions.map((sess, i) => {
                      const Icon = DEVICE_ICONS[sess.device] || Monitor;
                      return (
                        <div key={i} className="p-3 hover:bg-cream/40 flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4 text-emerald-700" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              <span className="text-base leading-none">{flagFromCountry(sess.country)}</span>
                              {sess.user_email && (
                                <Badge className="bg-gold-gradient text-rolex-green text-[9px] border-none">
                                  <Mail className="w-2.5 h-2.5 inline mr-1" /> connecté
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-[9px]">{sess.browser || '—'}</Badge>
                              <span className="text-[10px] text-rolex-green/55">{fmtRel(sess.last_seen)}</span>
                            </div>
                            <p className="text-xs text-rolex-green/80 truncate">
                              {sess.user_email || sess.session_id?.substring(0, 16) + '…'}
                            </p>
                            <p className="font-mono text-[10px] text-rolex-green/60 truncate">
                              📍 {sess.last_path}{sess.pageview_count ? ` · ${sess.pageview_count} vues` : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gold/20 bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gold-dark" /> Top pages (30 jours)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {(!stats.top_pages || stats.top_pages.length === 0) ? (
                  <p className="p-10 text-center text-sm text-rolex-green/55">Pas encore assez de données</p>
                ) : (
                  <div className="divide-y divide-gold/10">
                    {stats.top_pages.map((p, i) => {
                      const maxViews = stats.top_pages[0].views;
                      const pct = Math.round((p.views / maxViews) * 100);
                      return (
                        <div key={i} className="p-3 hover:bg-cream/40">
                          <div className="flex justify-between items-center gap-2 mb-1">
                            <p className="font-mono text-xs text-rolex-green truncate flex-1">{p.path}</p>
                            <span className="font-mono font-bold text-emerald-700 text-sm">{fmt(p.views)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[10px] text-rolex-green/55 font-mono">{fmt(p.uniques)} uniq.</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Devices + Browsers + Countries */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Card className="border border-gold/20 bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Monitor className="w-4 h-4 text-gold-dark" /> Appareils</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(stats.devices || []).map((d, i) => {
                  const Icon = DEVICE_ICONS[d.device] || Monitor;
                  const pct = Math.round((d.count / totalDevice) * 100);
                  return (
                    <div key={i}>
                      <div className="flex items-center gap-2 text-xs">
                        <Icon className="w-3.5 h-3.5 text-rolex-green/70" />
                        <span className="capitalize text-rolex-green font-semibold flex-1">{d.device || 'inconnu'}</span>
                        <span className="font-mono text-rolex-green/65">{fmt(d.count)} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mt-1">
                        <div className="h-full bg-gold-gradient" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border border-gold/20 bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Globe className="w-4 h-4 text-gold-dark" /> Navigateurs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(stats.browsers || []).map((b, i) => {
                  const pct = Math.round((b.count / totalBrowser) * 100);
                  return (
                    <div key={i}>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-semibold text-rolex-green flex-1">{b.browser || 'Inconnu'}</span>
                        <span className="font-mono text-rolex-green/65">{fmt(b.count)} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mt-1">
                        <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border border-gold/20 bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Globe className="w-4 h-4 text-gold-dark" /> Pays</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {(stats.countries || []).length === 0 ? (
                  <p className="text-xs text-rolex-green/55 italic text-center py-3">Données pays disponibles uniquement via Cloudflare</p>
                ) : (
                  stats.countries.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-base leading-none">{flagFromCountry(c.country)}</span>
                      <span className="font-mono text-rolex-green flex-1">{c.country}</span>
                      <span className="font-mono font-bold text-rolex-green">{fmt(c.count)}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top referrers */}
          {stats.top_referrers && stats.top_referrers.length > 0 && (
            <Card className="border border-gold/20 bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><ExternalLink className="w-4 h-4 text-gold-dark" /> Sources de trafic (30 jours)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {stats.top_referrers.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs p-2 rounded-lg hover:bg-cream/40">
                    <Badge className="bg-stone-200 text-stone-700 border-none font-mono">{i + 1}</Badge>
                    <span className="font-mono text-rolex-green/85 truncate flex-1" title={r.referrer}>{r.referrer}</span>
                    <span className="font-mono font-bold text-emerald-700">{fmt(r.count)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

function KpiCard({ label, value, sub, icon, tone = 'emerald' }) {
  const tones = {
    emerald: { card: 'bg-emerald-50 border-emerald-200', iconBg: 'bg-emerald-500 text-white' },
    gold:    { card: 'bg-gold/10 border-gold/40',         iconBg: 'bg-gold-gradient text-rolex-green' },
    blue:    { card: 'bg-blue-50 border-blue-200',        iconBg: 'bg-blue-500 text-white' },
    dark:    { card: 'bg-rolex-green text-cream border-rolex-green', iconBg: 'bg-gold-gradient text-rolex-green' },
  };
  const t = tones[tone] || tones.emerald;
  const isDark = tone === 'dark';
  return (
    <Card className={`border-2 ${t.card}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-gold' : 'text-rolex-green/70'}`}>{label}</p>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.iconBg}`}>{icon}</div>
        </div>
        <p className={`font-mono text-3xl font-extrabold ${isDark ? 'text-gold-gradient' : 'text-rolex-green'} tabular-nums`}>{value}</p>
        <p className={`text-[11px] mt-1 ${isDark ? 'text-cream/65' : 'text-rolex-green/65'}`}>{sub}</p>
      </CardContent>
    </Card>
  );
}

function Sparkline({ points = [] }) {
  if (!points.length) return <p className="text-xs text-rolex-green/45 italic">Aucune donnée</p>;
  const max = Math.max(1, ...points.map((p) => p.visitors || 0));
  const W = 800;
  const H = 80;
  const stepX = W / Math.max(1, points.length - 1);
  const path = points.map((p, i) => {
    const x = i * stepX;
    const y = H - ((p.visitors || 0) / max) * (H - 4) - 2;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const areaPath = `${path} L ${W},${H} L 0,${H} Z`;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-24">
        <defs>
          <linearGradient id="vsparkline" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#vsparkline)" />
        <path d={path} fill="none" stroke="#059669" strokeWidth="1.5" />
        {points.map((p, i) => {
          if (i % 5 !== 0 && i !== points.length - 1) return null;
          const x = i * stepX;
          const y = H - ((p.visitors || 0) / max) * (H - 4) - 2;
          return <circle key={i} cx={x} cy={y} r="2" fill="#059669" />;
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-rolex-green/55 font-mono mt-1">
        <span>{points[0]?.date}</span>
        <span className="font-bold text-emerald-700">Total : {fmt(points.reduce((a, b) => a + b.visitors, 0))} visiteurs</span>
        <span>{points[points.length - 1]?.date}</span>
      </div>
    </div>
  );
}
